/** @jsx jsx */
import { useState, useCallback } from 'react';
import { jsx } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button';
import { IconButton } from '@atlaskit/button/new';
import ChevronDownIcon from '@atlaskit/icon/utility/chevron-down';
import Form, { Field, type FormApi } from '@atlaskit/form';
import SendIcon from '@atlaskit/icon/core/send';
import { RovoIcon } from '@atlaskit/logo';
import { ConversationStarterPill } from '@atlaskit/rovo-agent-components';
import Popup from '@atlaskit/popup';
import { Box, Inline, Stack, xcss } from '@atlaskit/primitives';
import { usePublish } from '@atlaskit/rovo-triggers';
import Textfield from '@atlaskit/textfield';
import Tooltip from '@atlaskit/tooltip';

import {
	CONVERSATION_ASSISTANT_CONTAINER_ID,
	CONVERSATION_ASSISTANT_CONTAINER_WIDTH,
} from '@atlassian/conversation-assistant/utils';

import { useRightSidebarContext } from '@confluence/page-layout-context';

const messages = defineMessages({
	rovoChatButtonText: {
		id: 'highlight-actions.rovo-chat.button-text',
		defaultMessage: 'Chat',
		description: 'Text for button that opens the Rovo Chat sidebar',
	},
	rovoChatOpenChatTooltip: {
		id: 'highlight-actions.rovo-chat-open-chat.tooltip',
		defaultMessage: 'Open Chat',
		description: 'Tooltip for button that opens the Rovo Chat sidebar',
	},
	rovoChatMoreOptionsTooltip: {
		id: 'highlight-actions.rovo-chat-more-options.tooltip',
		defaultMessage: 'Chat recommendations',
		description: 'Tooltip label for the dropdown menu to show more options',
	},
	rovoChatExplainOptionText: {
		id: 'highlight-actions.rovo-chat-explain-option.text',
		defaultMessage: 'Explain',
		description: 'Text for the "Explain" option in the Rovo Chat more options popup',
	},
	rovoChatSummarizeOptionText: {
		id: 'highlight-actions.rovo-chat-summarize-option.text',
		defaultMessage: 'Summarize',
		description: 'Text for the "Summarize" option in the Rovo Chat more options popup',
	},
	rovoChatSummarizePrompt: {
		id: 'highlight-actions.rovo-chat-summarize-prompt',
		defaultMessage: 'Summarize this: {selectedText}',
		description: 'Prompt for the "Summarize" option in the Rovo Chat more options popup',
	},
	rovoChatExplainPrompt: {
		id: 'highlight-actions.rovo-chat-explain-prompt',
		defaultMessage: 'Explain this: {selectedText}',
		description: 'Prompt for the "Explain" option in the Rovo Chat more options popup',
	},
	rovoChatCustomPromptPlaceholder: {
		id: 'highlight-actions.rovo-chat-custom-prompt.placeholder',
		defaultMessage: 'Ask AI',
		description: 'Placeholder text for the custom prompt input in the Rovo Chat more options popup',
	},
});

const popupContainerStyles = xcss({
	padding: 'space.300',
});

const customFieldStyles = xcss({
	// The field component has a default marginBlockStart of 'space.100' which we need to override
	marginBlockStart: 'space.negative.100',
});

interface FormValues {
	prompt: string;
}

export const ChatWithRovoActions = ({ selectedText }: { selectedText: string }) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();
	const publish = usePublish('ai-mate');
	const rightSidebarContext = useRightSidebarContext();
	const { panelId } = rightSidebarContext.getCurrent() || {};
	const [isOpen, setIsOpen] = useState(false);
	const onClose = useCallback(() => {
		setIsOpen(false);
	}, [setIsOpen]);

	const isActive = panelId === CONVERSATION_ASSISTANT_CONTAINER_ID;

	const handleClickChat = useCallback(() => {
		if (!isActive) {
			rightSidebarContext.open(
				CONVERSATION_ASSISTANT_CONTAINER_ID,
				CONVERSATION_ASSISTANT_CONTAINER_WIDTH,
			);
		}

		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: 'highlightActionsMenu',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'highlightActionsMenuRovoChatButton',
				type: 'ui',
				attributes: {
					isRovoActive: isActive,
				},
			},
		}).fire();
	}, [isActive, rightSidebarContext, createAnalyticsEvent]);

	const handlePromptSubmit = useCallback(
		(values: FormValues, form: FormApi<FormValues>) => {
			publish({
				type: 'chat-new',
				source: 'confluence-context-menu',
				data: {
					name: values.prompt.substring(0, 30),
					dialogues: [],
					prompt: values.prompt,
				},
			});
			form.reset();
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					source: 'highlightActionsMenu',
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'highlightActionsMenuSubmitPromptButton',
					type: 'ui',
				},
			}).fire();
		},
		[publish, createAnalyticsEvent],
	);

	const renderPopupContent = () => {
		return (
			<Stack testId="context-menu-rovo-chat-popup" xcss={popupContainerStyles} space="space.150">
				<ConversationStarterPill
					testId="context-menu-rovo-chat-summarize-option"
					onClick={() => {
						publish({
							type: 'chat-new',
							source: 'confluence-context-menu',
							data: {
								name: selectedText.substring(0, 30),
								dialogues: [],
								prompt: formatMessage(messages.rovoChatSummarizePrompt, {
									selectedText,
								}),
							},
						});
						createAnalyticsEvent({
							type: 'sendUIEvent',
							data: {
								source: 'highlightActionsMenu',
								action: 'clicked',
								actionSubject: 'button',
								actionSubjectId: 'highlightActionsMenuConvoStarterButton',
								type: 'ui',
								attributes: {
									actionType: 'summarize',
								},
							},
						}).fire();
					}}
				>
					{formatMessage(messages.rovoChatSummarizeOptionText)}
				</ConversationStarterPill>
				<ConversationStarterPill
					testId="context-menu-rovo-chat-explain-option"
					onClick={() => {
						publish({
							type: 'chat-new',
							source: 'confluence-context-menu',
							data: {
								name: selectedText.substring(0, 30),
								dialogues: [],
								prompt: formatMessage(messages.rovoChatExplainPrompt, {
									selectedText,
								}),
							},
						});
						createAnalyticsEvent({
							type: 'sendUIEvent',
							data: {
								source: 'highlightActionsMenu',
								action: 'clicked',
								actionSubject: 'button',
								actionSubjectId: 'highlightActionsMenuConvoStarterButton',
								type: 'ui',
								attributes: {
									actionType: 'explain',
								},
							},
						}).fire();
					}}
				>
					{formatMessage(messages.rovoChatExplainOptionText)}
				</ConversationStarterPill>
				<Form onSubmit={handlePromptSubmit}>
					{({ formProps }) => (
						<form {...formProps}>
							<Box xcss={customFieldStyles}>
								<Field name="prompt">
									{({ fieldProps: { value, ...fieldPropsRest }, meta: { dirty } }) => (
										<Textfield
											{...fieldPropsRest}
											testId="context-menu-rovo-chat-custom-prompt"
											id="context-menu-rovo-chat-custom-prompt"
											autoComplete="off"
											elemAfterInput={
												<Box paddingInlineEnd="space.150">
													<IconButton
														testId="context-menu-rovo-chat-custom-prompt-submit"
														isDisabled={!dirty || !value}
														label=""
														type="submit"
														icon={SendIcon}
														spacing="compact"
													/>
												</Box>
											}
											placeholder={formatMessage(messages.rovoChatCustomPromptPlaceholder)}
										/>
									)}
								</Field>
							</Box>
						</form>
					)}
				</Form>
			</Stack>
		);
	};

	return (
		<Inline>
			<Tooltip content={formatMessage(messages.rovoChatOpenChatTooltip)}>
				{(tooltipProps) => (
					<Button
						testId="context-menu-rovo-chat-button"
						{...tooltipProps}
						appearance="subtle"
						iconBefore={<RovoIcon size="small" />}
						onClick={handleClickChat}
					>
						{formatMessage(messages.rovoChatButtonText)}
					</Button>
				)}
			</Tooltip>
			<Popup
				placement="bottom-start"
				isOpen={isOpen}
				onClose={onClose}
				autoFocus={false}
				content={renderPopupContent}
				trigger={(triggerProps) => (
					<div {...triggerProps}>
						<Tooltip content={formatMessage(messages.rovoChatMoreOptionsTooltip)}>
							{(tooltipProps) => (
								<Button
									{...tooltipProps}
									id="context-menu-rovo-chat-more-options"
									testId="context-menu-rovo-chat-more-options"
									aria-expanded={isOpen}
									appearance="subtle"
									isSelected={isOpen}
									onClick={() => {
										setIsOpen(!isOpen);
										createAnalyticsEvent({
											type: 'sendUIEvent',
											data: {
												source: 'highlightActionsMenu',
												action: 'clicked',
												actionSubject: 'button',
												actionSubjectId: 'highlightActionsMenuRovoChatMoreOptionsButton',
												type: 'ui',
											},
										}).fire();
									}}
									iconBefore={
										<ChevronDownIcon label={formatMessage(messages.rovoChatMoreOptionsTooltip)} />
									}
								/>
							)}
						</Tooltip>
					</div>
				)}
			/>
		</Inline>
	);
};
